import { call, fetchAllPages, type PageReq } from '../../shared/api.ts'
import { messageOf } from '../../shared/queries.ts'
import { ORDER_BULK_MAX, type OrderFilters, type apiAr, type apiDto, type apiOrder, type apiPricing } from '@rs/contracts'

/**
 * ══ CỬA GỌI REST CỦA ĐƠN HÀNG (F10) ═══════════════════════════════════════════════════════════
 * Mọi luật — phạm vi xem, khoá theo trạng thái, ai được huỷ, giá chào tính từ dòng doanh thu —
 * nằm ở `@rs/order`. Màn KHÔNG kiểm lại; kiểm lại là dựng bản sao thứ hai rồi hai bản lệch nhau.
 */
export type OrderRow = apiDto.OrderDto

const BASE = '/api/v1/az/orders'

export const fetchOrders = (): Promise<OrderRow[]> => fetchAllPages<OrderRow>(BASE)

/* ══ 📦 BẢNG ĐƠN HÀNG LỌC Ở MÁY CHỦ (đợt Đ4) — `POST /az/orders/bang*` ══════════════════════════════════════════
   POST vì bộ lọc mang cả cột mã dán từ Excel — quá dài cho chuỗi truy vấn. GỒM đơn hệ cũ với người có quyền xem. */

/** Một dòng bảng — đơn (có `legacy`) + trạng thái tiền + tên · mã · MST khách của chính đơn. */
export type OrderBoardRow = apiDto.OrderBoardRowDto
export type OrderBoardSummary = apiDto.OrderBoardSummaryDto
export type OrderBoardExcel = apiDto.OrderBoardExcelDto

const post = <T>(url: string, body: unknown): Promise<T> => call<T>(url, { method: 'POST', body: JSON.stringify(body) })

/** Một TRANG (keyset hai chiều) — `useServerPager` truyền `limit`/`cursor`/`dir`. Con trỏ rỗng KHÔNG gửi lên. */
export const fetchOrderBoard = (filters: OrderFilters, req: PageReq): Promise<apiDto.OrderBoardPageDto> =>
  post<apiDto.OrderBoardPageDto>(`${BASE}/bang`, {
    filters, limit: req.limit, dir: req.dir, ...(req.cursor === null || req.cursor === '' ? {} : { cursor: req.cursor }),
  })

/**
 * Cửa nhận `ids` (đọc lại · Excel đang tick · đổi trạng thái đơn cũ) nhận tối đa `ORDER_BULK_MAX` id một lượt, còn cửa trang
 * `…/bang` gửi kèm `limit` (trần `PageQuery` 200), mà tick tay thì không có trần ⇒ chia lô 200 Ở ĐÂY, nơi gọi không phải nhớ.
 */
export const ID_BATCH = Math.min(ORDER_BULK_MAX, 200)
export const idBatches = <T>(xs: readonly T[]): T[][] => {
  const out: T[][] = []
  for (let i = 0; i < xs.length; i += ID_BATCH) out.push(xs.slice(i, i + ID_BATCH))
  return out
}

/** Đọc LẠI đúng các đơn này (bỏ qua bộ lọc, giữ phạm vi) — kể cả đơn hệ cũ. Chia lô 200, giữ thứ tự từng lô. */
export const fetchOrderBoardRows = async (ids: readonly string[]): Promise<OrderBoardRow[]> =>
  (await Promise.all(idBatches(ids).map((b) =>
    post<apiDto.OrderBoardPageDto>(`${BASE}/bang`, { filters: {}, limit: ID_BATCH, ids: b })))).flatMap((p) => p.rows)

/** Một đơn cho hộp chi tiết: dòng mới nhất + cơ cấu doanh thu (đơn hệ cũ không có cửa `chi-tiet`). */
export const fetchOrderBoardDetail = async (
  id: string,
): Promise<{ row: OrderBoardRow | null; totals: NonNullable<apiDto.OrderBoardPageDto['totals']>[number] | null }> => {
  const p = await post<apiDto.OrderBoardPageDto>(`${BASE}/bang`, { filters: {}, limit: 1, ids: [id] })
  return { row: p.rows.find((o) => o.id === id) ?? null, totals: p.totals?.find((x) => x.jobId === id) ?? null }
}

export const fetchOrderBoardSummary = (filters: OrderFilters): Promise<OrderBoardSummary> =>
  post<OrderBoardSummary>(`${BASE}/bang/tom-tat`, { filters })

/**
 * ⬇ Mẫu AMIS · Công trình: MỌI đơn của tập đang lọc — GIỮ cả khoảng ngày của màn (bản cũ đưa `rows` đã chịu lọc ngày vào hộp
 * rồi hộp lọc tiếp theo kỳ, `Az.tsx:2433`; kiểm kê 23/10 R0700 — trước đó bỏ từ/đến nên đơn ngoài khoảng của danh sách lọt vào
 * tệp), kèm ngày bốc THỰC TẾ ‖ ngày kế hoạch như bản cũ (`Az.tsx:2438` `azStopDates(j).actualPickup || j.planDate`; in-excel-15).
 * Đi theo con trỏ, mỗi lượt 200 dòng.
 */
export const fetchOrderMisaScope = async (filters: OrderFilters): Promise<{ id: string; date: string | null }[]> => {
  const out: { id: string; date: string | null }[] = []
  let cursor: string | null = null
  for (let n = 0; n < 500; n += 1) {
    const page: apiDto.OrderBoardPageDto = await fetchOrderBoard(filters, { limit: 200, cursor, dir: 'next' })
    for (const o of page.rows) {
      const first = ((o.pickups ?? []) as Record<string, unknown>[])[0]
      const actual = typeof first?.['actualAt'] === 'string' ? first['actualAt'].slice(0, 10) : ''
      out.push({ id: o.id, date: actual !== '' ? actual : o.planDate })
    }
    if (page.nextCursor === null) return out
    cursor = page.nextCursor
  }
  throw new Error('Danh sách đơn quá dài — hãy thu hẹp bộ lọc.')
}

/** "Chọn hết" — ≤ 200 dòng đầu của tập đang lọc, đúng thứ tự bảng. */
export const selectAllOrdersApi = async (filters: OrderFilters): Promise<OrderBoardRow[]> =>
  (await post<apiDto.OrderBoardSelectAllDto>(`${BASE}/bang/chon-het`, { filters })).rows

/** ⬇ Một LÔ của tệp Excel theo bộ lọc (hoặc đúng các đơn đang tick). Màn gọi lại tới khi `nextCursor = null`. */
export const fetchOrderBoardExcel = (
  filters: OrderFilters, opts: { ids?: readonly string[]; cursor?: string },
): Promise<OrderBoardExcel> => post<OrderBoardExcel>(`${BASE}/bang/excel`, {
  filters,
  ...(opts.ids === undefined ? {} : { ids: opts.ids }),
  ...(opts.cursor === undefined ? {} : { cursor: opts.cursor }),
})

/** Đơn gần nhất của MỘT khách cho `OrderModal` điền sẵn — KHÔNG gồm đơn hệ cũ. `route` so bỏ khoảng trắng/hoa thường. */
export const fetchLatestOrders = async (customerId: string, opts: { route?: string; pack?: string } = {}): Promise<OrderRow[]> => {
  const p = new URLSearchParams()
  if ((opts.route ?? '') !== '') p.set('route', opts.route ?? '')
  if ((opts.pack ?? '') !== '') p.set('pack', opts.pack ?? '')
  const q = p.toString()
  return (await call<apiDto.LatestOrdersDto>(`${BASE}/khach/${customerId}/gan-nhat${q === '' ? '' : `?${q}`}`)).rows
}

/** Tỉnh/thành đã gõ ở điểm lấy/giao (trong phạm vi xem) — `OrderModal` lọc ra tỉnh nước ngoài cho dịch vụ QT. */
export const fetchOrderProvinces = async (): Promise<string[]> =>
  (await call<apiDto.OrderProvincesDto>(`${BASE}/tinh-da-dung`)).rows

/**
 * Đổi trạng thái ĐƠN HỆ CŨ — CHỈ trạng thái, không gỡ chặng, không kiểm HĐ, không luồng nào khác. Chia lô 200 TUẦN
 * TỰ (mỗi lô một giao dịch ở máy chủ), cộng kết quả.
 * ⚠ KHÔNG NÉM khi một lô hỏng: các lô trước ĐÃ GHI (giao dịch riêng), ném lỗi là vứt mất kết quả của chúng và người
 *   dùng chỉ thấy câu lỗi trong khi dữ liệu đã đổi. Dừng ở lô hỏng, trả phần đã ghi + `error` (+ số đơn chưa gửi).
 */
export const setLegacyStatusApi = async (
  status: string, jobs: readonly { jobId: string; version: number }[],
): Promise<apiDto.SetLegacyStatusResultDto & { error?: string; notSent?: number }> => {
  const out: apiDto.SetLegacyStatusResultDto = { changed: 0, skipped: [] }
  const batches = idBatches(jobs)
  for (const [i, b] of batches.entries()) {
    try {
      const r = await post<apiDto.SetLegacyStatusResultDto>(`${BASE}/trang-thai-he-cu`, { status, jobs: b })
      out.changed += r.changed
      out.skipped.push(...r.skipped)
    } catch (e: unknown) {
      return { ...out, error: messageOf(e), notSent: batches.slice(i).reduce((n, x) => n + x.length, 0) }
    }
  }
  return out
}
/** Đơn của MỘT khách — hồ sơ khách (bản cũ lọc cả kho trong trình duyệt). */
export const fetchOrdersOf = (customerId: string): Promise<OrderRow[]> =>
  fetchAllPages<OrderRow>(`${BASE}?customerId=${customerId}`)

export const createOrderApi = (body: Record<string, unknown>): Promise<OrderRow> =>
  call<OrderRow>(BASE, { method: 'POST', body: JSON.stringify(body) })

export const updateOrderApi = (
  id: string, version: number, patch: Record<string, unknown>,
): Promise<void> =>
  call<void>(`${BASE}/${id}`, { method: 'POST', body: JSON.stringify({ ...patch, version }) })

/**
 * Đổi trạng thái MỘT đơn — cửa RIÊNG, máy chủ áp CÙNG luật với `/doi-trang-thai` (phải có chặng · đã giao thì khoá ·
 * chỉ admin huỷ đơn đã xác nhận · huỷ phải xác nhận GỠ chặng · giao thì đồng bộ chặng). `extra` mang đúng ba ô đó.
 */
export const setOrderStatusApi = (
  id: string, version: number, status: string,
  extra: Omit<apiOrder.OrderStatusBodyT, 'version' | 'status'> = {},
): Promise<void> =>
  call<void>(`${BASE}/${id}/status`, { method: 'POST', body: JSON.stringify({ ...extra, version, status }) })

/**
 * Chép đơn. HAI CÁCH GỌI, ứng với hai thao tác của bản cũ trên cùng nút ⧉:
 *   · truyền một SỐ  → N bản y hệt (tuỳ chọn dời ngày chạy).
 *   · truyền MỘT MẢNG → mỗi phần tử là một bản, mang số cont · seal · loại · tiền cước · ngày
 *     bốc · ngày giao RIÊNG. Đây là hộp thoại mở khi bấm ĐÚP.
 */
export const copyOrderApi = (
  id: string, version: number, times: number | apiOrder.CopyOrderRowT[], planDate?: string,
): Promise<{ rows: OrderRow[] }> =>
  call<{ rows: OrderRow[] }>(`${BASE}/${id}/copy`, {
    method: 'POST',
    body: JSON.stringify(Array.isArray(times)
      ? { version, rows: times }
      : (planDate === undefined ? { version, times } : { version, times, planDate })),
  })

/**
 * ⭐ TẠO ĐƠN + CHỐT BÁO GIÁ (rà 16/10 C2). Gọi khi sale bấm LƯU ở form đơn nạp sẵn từ báo giá — một giao dịch ở máy chủ:
 * tạo đơn (đủ thông tin form) rồi chốt báo giá trỏ vào đơn đó. Đóng form thì không gọi gì, báo giá giữ nguyên
 * (bản cũ `Az.tsx:6778-6783`).
 */
export const orderFromQuoteApi = (
  quoteId: string, version: number, body: Record<string, unknown>,
): Promise<OrderRow> =>
  call<OrderRow>(`${BASE}/from-quote`, {
    method: 'POST',
    body: JSON.stringify({ ...body, quoteId, version }),
  })

/* ══ GIÁ CHO FORM ĐƠN HÀNG ═══════════════════════════════════════════════════════════════════════ */

export type OrderPriceLookup = apiDto.OrderPriceLookupDto
export type OrderPriceRow = apiDto.OrderPriceRowDto
export type OrderAllocLookup = apiDto.OrderAllocLookupDto

export type OrderPriceParams = apiPricing.OrderPriceQueryT

export const orderPriceQuery = (p: OrderPriceParams): string =>
  new URLSearchParams(Object.entries(p).filter(([, v]) => v !== undefined && v !== '') as [string, string][]).toString()

/** Giá công bố đang áp dụng theo ngày lấy hàng + giá trọn gói đường sắt (khi có hai ga khác nhau). */
export const fetchOrderPrices = (query: string): Promise<OrderPriceLookup> =>
  call<OrderPriceLookup>(`/api/v1/az/gia-cong-bo/don-hang?${query}`)

/** "Lấy giá" phân bổ dầu + khấu hao vỏ lạnh theo ngày bốc · loại cont · tuyến. */
export const fetchAllocLookup = (onDate: string, contType: string, routeName: string): Promise<OrderAllocLookup> =>
  call<OrderAllocLookup>(`/api/v1/az/gia-phan-bo/tra-cuu?${new URLSearchParams({ onDate, contType, routeName }).toString()}`)

/* ══ 🧾 YÊU CẦU XUẤT CHỨNG TỪ BÁN NHIỀU ĐƠN (`BillRequestModal`) ════════════════════════════════════ */

export type StatementRequestPreview = apiDto.StatementRequestPreviewDto

/** Xem trước: tổng khách trả · BKS · ngày bốc · tuyến gọn · lý do KHÔNG xin được — máy chủ tính, màn chỉ hiện. */
export const fetchStatementRequestPreview = (jobIds: readonly string[]): Promise<StatementRequestPreview> =>
  call<StatementRequestPreview>(`${BASE}/yeu-cau-xuat-ct/xem-truoc?jobIds=${jobIds.join(',')}`)

/** Gửi yêu cầu ⇒ máy chủ lập NGAY bảng kê bán nháp (tách hóa đơn ⇒ hai bảng kê). */
export const requestStatementApi = (body: apiAr.StatementRequestBodyT): Promise<{ statements: apiDto.ArStatementDto[] }> =>
  call<{ statements: apiDto.ArStatementDto[] }>(`${BASE}/yeu-cau-xuat-ct`, { method: 'POST', body: JSON.stringify(body) })

/* ══ ĐỔI TRẠNG THÁI ĐƠN ĐÚNG QUY TRÌNH (`setJobStatus`) ══════════════════════════════════════════════════════════ */

export type StatusFlowCheck = apiDto.StatusFlowCheckDto

/** `reeferGuard`: lối Điều hành ĐH / Chi tiết đơn — máy chủ chặn "Đã giao"/"Hủy" đơn Vỏ lạnh theo dõi (T11). */
export const checkStatusFlowApi = (status: string, jobIds: readonly string[], reeferGuard = false): Promise<StatusFlowCheck> =>
  call<StatusFlowCheck>(`${BASE}/doi-trang-thai/kiem-tra?status=${status}&jobIds=${jobIds.join(',')}${reeferGuard ? '&reeferGuard=true' : ''}`)

export const statusFlowApi = (body: apiOrder.StatusFlowBodyT): Promise<{ changed: string[]; skipped: { ref: string; reason: string }[] }> =>
  call<{ changed: string[]; skipped: { ref: string; reason: string }[] }>(`${BASE}/doi-trang-thai`, { method: 'POST', body: JSON.stringify(body) })

/**
 * Đặt trạng thái KHÔNG hỏi lại — bản cũ `setJobStatus(..., skipConfirm = true)` ngay sau khi bắn chặng từ
 * luồng đổi trạng thái (`Az.tsx:2466`). Vẫn qua cửa kiểm của máy chủ; trả các đơn bị bỏ qua kèm lý do.
 */
export const statusAfterDispatchApi = async (status: string, jobIds: readonly string[]): Promise<{ ref: string; reason: string }[]> => {
  const c = await checkStatusFlowApi(status, jobIds)
  const live = c.rows.filter((r) => r.skip === null)
  const skipped = c.rows.filter((r) => r.skip !== null && r.orderStatus !== status).map((r) => ({ ref: r.ref, reason: r.skip ?? '' }))
  if (live.length === 0) return skipped
  const r = await statusFlowApi({ status: status as apiOrder.StatusFlowBodyT['status'], jobs: live.map((x) => ({ jobId: x.jobId, version: x.version })) })
  return [...skipped, ...r.skipped]
}

/** 👁 Chi tiết đơn — cơ cấu doanh thu + hóa đơn bán gắn với đơn + trạng thái thu tiền (máy chủ tính). */
export type OrderDetail = apiDto.OrderDetailDto
export const fetchOrderDetail = (id: string): Promise<OrderDetail> => call<OrderDetail>(`${BASE}/${id}/chi-tiet`)

/* ══ 📥 NHẬP ĐƠN HÀNG LOẠT · SỬA DOANH THU HÀNG LOẠT (`BulkOrderImport` · `BulkRevenueImport`) ═════════════════════════════ */

export type BulkOrderImport = apiDto.BulkOrderImportDto
export const bulkOrderImportApi = (body: apiOrder.BulkCreateOrderBodyT): Promise<BulkOrderImport> =>
  call<BulkOrderImport>(`${BASE}/nhap-hang-loat`, { method: 'POST', body: JSON.stringify(body) })

export type BulkRevenueResult = apiDto.BulkRevenueResultDto
export const bulkRevenueApi = (body: apiOrder.BulkRevenueBodyT): Promise<BulkRevenueResult> =>
  call<BulkRevenueResult>(`${BASE}/sua-doanh-thu-hang-loat`, { method: 'POST', body: JSON.stringify(body) })
